import { type InfiniteData, type QueryClient } from '@tanstack/react-query';
import produce from 'immer';

import { type AgentSessionResponse, type ListAgentSessionsResponse } from '@air/server-sdk';

import {
  buildPrivateWorkspaceAgentSessionsListArchivedParams,
  buildPrivateWorkspaceAgentSessionsListInfiniteParams,
  getPrivateWorkspaceAgentSessionsListInfiniteKey,
  type PrivateWorkspaceAgentSessionsListInfiniteParams,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionsListInfinite';

type PrivateWorkspaceAgentSessionsListCacheWriteOptions = {
  /** When true (default), abort in-flight list refetches before writing — use for optimistic mutations only. */
  cancelInFlightListFetches?: boolean;
};

const WORKSPACE_AGENT_SESSION_LIST_ONLY_RESPONSE_FIELDS = [
  'latestAgentMessage',
] as const satisfies readonly (keyof AgentSessionResponse)[];

/**
 * Session GET and session list rows share `AgentSessionResponse`, but list payloads can include
 * fields the GET omits. Stall refresh must not overlay those onto an existing cached row.
 */
export const buildWorkspaceAgentSessionListCachePatchFromSessionGet = (
  session: AgentSessionResponse,
): Partial<AgentSessionResponse> => {
  const patch: Partial<AgentSessionResponse> = { ...session };

  for (const field of WORKSPACE_AGENT_SESSION_LIST_ONLY_RESPONSE_FIELDS) {
    delete patch[field];
  }

  return patch;
};

export const findPrivateWorkspaceAgentSessionInListCache = (
  queryClient: QueryClient,
  params: PrivateWorkspaceAgentSessionsListInfiniteParams,
  sessionId: string,
): AgentSessionResponse | undefined => {
  const cached = queryClient.getQueryData<InfiniteData<ListAgentSessionsResponse>>(
    getPrivateWorkspaceAgentSessionsListInfiniteKey(params),
  );

  if (!cached) {
    return undefined;
  }

  for (const page of cached.pages) {
    const session = page.data.find((item) => item.id === sessionId);

    if (session) {
      return session;
    }
  }

  return undefined;
};

export const removePrivateWorkspaceAgentSessionFromListCache = (
  queryClient: QueryClient,
  params: PrivateWorkspaceAgentSessionsListInfiniteParams,
  sessionId: string,
) => {
  queryClient.setQueryData<InfiniteData<ListAgentSessionsResponse>>(
    getPrivateWorkspaceAgentSessionsListInfiniteKey(params),
    (previous) => {
      if (!previous) {
        return previous;
      }

      return produce(previous, (draft) => {
        for (const page of draft.pages) {
          page.data = page.data.filter((session) => session.id !== sessionId);
        }
      });
    },
  );
};

export const upsertPrivateWorkspaceAgentSessionInListCache = (
  queryClient: QueryClient,
  params: PrivateWorkspaceAgentSessionsListInfiniteParams,
  session: AgentSessionResponse,
  options?: PrivateWorkspaceAgentSessionsListCacheWriteOptions,
) => {
  const listKey = getPrivateWorkspaceAgentSessionsListInfiniteKey(params);

  if (options?.cancelInFlightListFetches ?? true) {
    // Cancel in-flight list fetches so optimistic cache writes are not overwritten.
    void queryClient.cancelQueries({ queryKey: listKey });
  }

  queryClient.setQueryData<InfiniteData<ListAgentSessionsResponse>>(listKey, (previous) => {
    if (!previous) {
      return {
        pageParams: [''],
        pages: [
          {
            data: [session],
            // Unknown pagination — never claim the archived list is exhausted from a cache seed.
            pagination: { hasMore: true, cursor: null },
          },
        ],
      };
    }

    return produce(previous, (draft) => {
      let found = false;

      for (const page of draft.pages) {
        for (const [index, item] of page.data.entries()) {
          if (item.id === session.id) {
            page.data[index] = { ...item, ...session };
            found = true;
          }
        }
      }

      if (found) {
        return;
      }

      if (draft.pages[0]) {
        draft.pages[0].data.unshift(session);
        return;
      }

      draft.pages.push({ data: [session], pagination: { hasMore: true, cursor: null } });
    });
  });
};

export const movePrivateWorkspaceAgentSessionToArchivedListCache = (
  queryClient: QueryClient,
  workspaceId: string,
  sessionId: string,
  patch: Partial<AgentSessionResponse>,
  options?: PrivateWorkspaceAgentSessionsListCacheWriteOptions,
) => {
  const liveParams = buildPrivateWorkspaceAgentSessionsListInfiniteParams(workspaceId);
  const archivedParams = buildPrivateWorkspaceAgentSessionsListArchivedParams(workspaceId);
  const liveSession = findPrivateWorkspaceAgentSessionInListCache(queryClient, liveParams, sessionId);

  removePrivateWorkspaceAgentSessionFromListCache(queryClient, liveParams, sessionId);

  if (liveSession) {
    upsertPrivateWorkspaceAgentSessionInListCache(queryClient, archivedParams, { ...liveSession, ...patch }, options);
    return;
  }

  const archivedSession = findPrivateWorkspaceAgentSessionInListCache(queryClient, archivedParams, sessionId);

  if (archivedSession) {
    patchPrivateWorkspaceAgentSessionsListCache(queryClient, archivedParams, sessionId, patch);
    return;
  }

  // Session is gone from live cache and archived list was never loaded — refetch archived on next read.
  void queryClient.invalidateQueries({ queryKey: getPrivateWorkspaceAgentSessionsListInfiniteKey(archivedParams) });
};

export const movePrivateWorkspaceAgentSessionToLiveListCache = (
  queryClient: QueryClient,
  workspaceId: string,
  sessionId: string,
  patch: Partial<AgentSessionResponse>,
  options?: PrivateWorkspaceAgentSessionsListCacheWriteOptions,
) => {
  const liveParams = buildPrivateWorkspaceAgentSessionsListInfiniteParams(workspaceId);
  const archivedParams = buildPrivateWorkspaceAgentSessionsListArchivedParams(workspaceId);
  const archivedSession = findPrivateWorkspaceAgentSessionInListCache(queryClient, archivedParams, sessionId);

  removePrivateWorkspaceAgentSessionFromListCache(queryClient, archivedParams, sessionId);

  if (archivedSession) {
    upsertPrivateWorkspaceAgentSessionInListCache(queryClient, liveParams, { ...archivedSession, ...patch }, options);
    return;
  }

  const liveSession = findPrivateWorkspaceAgentSessionInListCache(queryClient, liveParams, sessionId);

  if (liveSession) {
    patchPrivateWorkspaceAgentSessionsListCache(queryClient, liveParams, sessionId, patch);
    return;
  }

  void queryClient.invalidateQueries({ queryKey: getPrivateWorkspaceAgentSessionsListInfiniteKey(liveParams) });
};

export const patchPrivateWorkspaceAgentSessionsListCache = (
  queryClient: QueryClient,
  params: PrivateWorkspaceAgentSessionsListInfiniteParams,
  sessionId: string,
  patch: Partial<AgentSessionResponse>,
) => {
  queryClient.setQueryData<InfiniteData<ListAgentSessionsResponse>>(
    getPrivateWorkspaceAgentSessionsListInfiniteKey(params),
    (previous) => {
      if (!previous) {
        return previous;
      }

      return produce(previous, (draft) => {
        for (const page of draft.pages) {
          for (const [index, session] of page.data.entries()) {
            if (session.id === sessionId) {
              page.data[index] = { ...session, ...patch };
            }
          }
        }
      });
    },
  );
};

/**
 * Chat stall-refresh GETs the open session only. Patch live or archived if the row is already
 * cached; otherwise seed live so chat `status` updates without refetching the full session list.
 */
export const applyWorkspaceAgentSessionToListCaches = (
  queryClient: QueryClient,
  workspaceId: string,
  session: AgentSessionResponse,
): void => {
  const listCacheWriteOptions = { cancelInFlightListFetches: false };
  const listPatch = buildWorkspaceAgentSessionListCachePatchFromSessionGet(session);

  if (session.status === 'archived') {
    movePrivateWorkspaceAgentSessionToArchivedListCache(
      queryClient,
      workspaceId,
      session.id,
      listPatch,
      listCacheWriteOptions,
    );
    return;
  }

  const liveParams = buildPrivateWorkspaceAgentSessionsListInfiniteParams(workspaceId);
  const archivedParams = buildPrivateWorkspaceAgentSessionsListArchivedParams(workspaceId);

  if (findPrivateWorkspaceAgentSessionInListCache(queryClient, liveParams, session.id)) {
    patchPrivateWorkspaceAgentSessionsListCache(queryClient, liveParams, session.id, listPatch);
    return;
  }

  if (findPrivateWorkspaceAgentSessionInListCache(queryClient, archivedParams, session.id)) {
    movePrivateWorkspaceAgentSessionToLiveListCache(
      queryClient,
      workspaceId,
      session.id,
      listPatch,
      listCacheWriteOptions,
    );
    return;
  }

  upsertPrivateWorkspaceAgentSessionInListCache(queryClient, liveParams, session, listCacheWriteOptions);
};
